import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getVisuals, CONF_ICON } from "./treemap-utils";

export function ActionHoverCard({ node, mousePos }) {
  if (!node || !mousePos) return null;
  const v = getVisuals(node.state, node.urgency);
  const conf = node.confidence || 0;
  const bd = node.biggest_driver;
  const primaryIssue = bd ? `${bd.source} (${bd.percentage}%)` : (node.drivers || [])[0] || "";
  const crossImpact = (node.cross_impacts || [])[0];

  const cardW = 320, cardH = 260;
  const left = Math.min(mousePos.x + 20, window.innerWidth - cardW - 20);
  const top = Math.min(mousePos.y + 16, window.innerHeight - cardH - 20);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed z-50 pointer-events-none"
      style={{ left, top, width: cardW }}
      data-testid="action-hover-card"
    >
      <div className="rounded-xl border overflow-hidden" style={{ background: "rgba(2,4,2,0.94)", borderColor: v.border, backdropFilter: "blur(20px)" }}>
        <div className="px-4 pt-3 pb-2 flex items-center justify-between">
          <Badge className={`${v.badge} border text-[10px] px-2 py-0.5`}>{v.label}</Badge>
          <span className="text-xs font-bold" style={{ color: v.accent }}>{node.metric}</span>
        </div>
        <div className="px-4 pb-3 space-y-2">
          {node.trend_duration && (
            <p className="text-[11px] text-white/35 flex items-center gap-1">
              {node.trend === "worsening" ? <ArrowUpRight className="w-3 h-3 text-red-400/60" /> :
               node.trend === "improving" ? <ArrowDownRight className="w-3 h-3 text-emerald-400/60" /> :
               <Minus className="w-3 h-3 text-white/30" />}
              {node.trend_duration}
            </p>
          )}
          {primaryIssue && (
            <div>
              <p className="text-[9px] uppercase tracking-wider text-white/25 mb-0.5">Primary Issue</p>
              <p className="text-[11px] text-white/70 leading-snug">{primaryIssue}</p>
            </div>
          )}
          {node.suggested_action && (
            <div className="rounded-lg px-2.5 py-1.5" style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.1)" }}>
              <p className="text-[9px] uppercase tracking-wider text-primary/50 mb-0.5">Suggested Action</p>
              <p className="text-[11px] text-white/80 leading-snug">{node.suggested_action}</p>
            </div>
          )}
          {node.impact_potential && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] uppercase tracking-wider text-white/25">Impact Potential</p>
                <p className="text-[11px] text-white/60">{node.impact_potential}</p>
              </div>
              <div className="text-right">
                <p className="text-[9px] uppercase tracking-wider text-white/25">Confidence</p>
                <p className="text-[11px] text-white/50">{CONF_ICON(conf)} {Math.round(conf * 100)}%</p>
              </div>
            </div>
          )}
          {node.real_world_implication && (
            <p className="text-[10px] text-white/30 italic leading-snug border-t border-white/5 pt-2">
              {node.real_world_implication}
            </p>
          )}
          {crossImpact && (
            <p className="text-[10px] text-amber-400/40 leading-snug">
              {crossImpact.description}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
